import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const completeAuth = useCallback(({ token, user: userData }) => {
    sessionStorage.setItem('d2d_token', token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setUser(userData);
    return userData;
  }, []);

  const fetchMe = useCallback(async () => {
    try {
      const res = await axios.get('/api/auth/me');
      setUser(res.data);
      return res.data;
    } catch {
      sessionStorage.removeItem('d2d_token');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem('d2d_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchMe().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const res = await axios.post('/api/auth/login', { email, password });
    return completeAuth(res.data);
  };

  const registerAccount = async (payload) => {
    const res = await axios.post('/api/auth/register', payload);
    return completeAuth(res.data);
  };

  const logout = () => {
    sessionStorage.removeItem('d2d_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  // Call this after any action that mutates user data (profile update, loyalty change, etc.)
  const refreshUser = useCallback(() => fetchMe(), [fetchMe]);

  return (
    <AuthContext.Provider value={{ user, loading, login, registerAccount, logout, setUser, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
